import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { supabase } from '@/lib/supabase';
import { useRows } from '@/hooks/useLive';
import { useSiteFilter } from '@/hooks/useSiteFilter';
import { allocatedHours, breakdown } from '@/lib/diary';
import { date as fmtDate, hours as fmtHours, humanise, isoDate, num as fmtNum, tonnes as fmtTonnes } from '@/lib/format';
import { useAuthStore } from '@/stores/authStore';
import { useSyncStore } from '@/stores/syncStore';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { StatTile } from '@/components/ui/StatTile';
import { StatusChip } from '@/components/ui/StatusChip';
import { SyncIssues } from '@/components/sync/SyncIssues';
import { ALL_SITES, QUARRY_SITE_ID, diarySiteOf } from '@/lib/fleet';
import type {
  DiaryActivity, DiaryStatus, DiaryTimeAllocation, Machine, MachineType, Shift, Site, TimeCategory,
} from '@/types/models';

/** The site last chosen on this device, so a branch supervisor's tablet
 *  opens on their branch. */
const SITE_KEY = 'diary.site';

const STATUS_TONE: Record<DiaryStatus, 'grey' | 'blue' | 'green' | 'red'> = {
  draft: 'grey', submitted: 'blue', approved: 'green', rejected: 'red',
};

interface Exception {
  exception_type: string;
  exception_date: string;
  fleet_no: string;
  detail: string;
}

/**
 * The day view. A supervisor works down the fleet for a date, so the list
 * is every active machine — captured or not. What is missing is as
 * important as what is there, which is why uncaptured machines are shown
 * rather than hidden.
 */
export function Diary() {
  const navigate = useNavigate();
  const [day, setDay] = useState(isoDate());
  const [shiftId, setShiftId] = useState<string>('');
  const [siteId, setSiteId] = useState<string>(
    () => localStorage.getItem(SITE_KEY) || QUARRY_SITE_ID);
  const [exceptions, setExceptions] = useState<Exception[]>([]);

  const machines = useRows<Machine>('machines', 'fleet_no');
  const types = useRows<MachineType>('machine_types', 'sort_order');
  const shifts = useRows<Shift>('shifts', 'sort_order');
  const categories = useRows<TimeCategory>('time_categories', 'sort_order');
  const sites = useRows<Site>('sites', 'name');
  const siteOk = useSiteFilter();
  const online = useSyncStore((s) => s.online);
  const issues = useSyncStore((s) => s.issues);
  const role = useAuthStore((s) => s.profile?.role);
  const canApprove = !!role && ['supervisor', 'manager', 'admin'].includes(role);
  const visibleShifts = useMemo(() => {
    const own = (shifts ?? []).filter((s) => s.is_active && (siteId === ALL_SITES ? siteOk(s.site_id || QUARRY_SITE_ID) : (s.site_id || QUARRY_SITE_ID) === siteId));
    // All sites groups by shift code; each row uses that site's corresponding id.
    return siteId === ALL_SITES ? own.filter((s, i) => own.findIndex((x) => x.code === s.code) === i) : own;
  }, [shifts, siteId, siteOk]);
  useEffect(() => {
    if (!visibleShifts.some((s) => s.id === shiftId)) setShiftId(visibleShifts[0]?.id ?? '');
  }, [visibleShifts, shiftId]);

  // 0053: the quarry, each concrete branch, or every site together.
  const siteOptions = useMemo(() => {
    const list = (sites ?? []).filter((s) => !s.deleted_at && s.is_active
      && (s.kind === 'quarry' || s.kind === 'branch') && siteOk(s.id));
    const quarry = list.filter((s) => s.kind === 'quarry');
    const branches = list.filter((s) => s.kind === 'branch');
    return [...quarry, ...branches];
  }, [sites, siteOk]);
  const siteName = (id: string | null | undefined) =>
    siteOptions.find((s) => s.id === (id || QUARRY_SITE_ID))?.name ?? '';

  // A remembered site that no longer exists falls back to the quarry.
  useEffect(() => {
    if (sites && siteOptions.length && siteId !== ALL_SITES
        && !(siteId === QUARRY_SITE_ID && siteOptions.some((s) => s.kind === 'quarry'))
        && !siteOptions.some((s) => s.id === siteId)) {
      setSiteId(siteOptions[0].id);
    }
  }, [sites, siteOptions, siteId]);

  function chooseSite(id: string) {
    setSiteId(id);
    localStorage.setItem(SITE_KEY, id);
  }

  // Diaries for the selected date, with their allocations and activities.
  const dayData = useLiveQuery(async () => {
    const diaries = (await db.daily_diary.where('diary_date').equals(day).toArray())
      .filter((d) => !d.deleted_at);
    const ids = diaries.map((d) => d.id);
    const allocs = ids.length
      ? (await db.diary_time_allocations.where('diary_id').anyOf(ids).toArray())
      : [];
    const acts = ids.length
      ? (await db.diary_activities.where('diary_id').anyOf(ids).toArray())
      : [];
    return { diaries, allocs, acts };
  }, [day]);

  // Data-quality warnings come from the server view; online only.
  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('v_data_exceptions')
        .select('*')
        .eq('exception_date', day)
        .limit(50);
      if (!cancelled && data) setExceptions(data as Exception[]);
    })();
    return () => { cancelled = true; };
  }, [day, online, dayData]);

  const rows = useMemo(() => {
    if (!machines || !dayData) return undefined;
    // 0053: the vehicles of the chosen site, or of the quarry and every
    // branch together.
    const allowed = new Set(siteOptions.map((s) => s.id));
    if (siteOptions.some((s) => s.kind === 'quarry')) allowed.add(QUARRY_SITE_ID);
    const active = machines.filter((m) => {
      if (!m.is_active || m.status === 'disposed') return false;
      const site = diarySiteOf(m);
      return allowed.has(site) && (siteId === ALL_SITES || site === siteId);
    });

    return active.map((m) => {
      const selectedShift = shifts?.find((s) => s.id === shiftId);
      const rowShift = siteId === ALL_SITES
        ? shifts?.find((s) => (s.site_id || QUARRY_SITE_ID) === diarySiteOf(m) && s.code === selectedShift?.code && s.is_active)?.id
        : shiftId;
      const diary = dayData.diaries.find(
        (d) => d.machine_id === m.id && (!rowShift || d.shift_id === rowShift),
      );
      const allocs = diary
        ? dayData.allocs.filter((a) => a.diary_id === diary.id)
        : ([] as DiaryTimeAllocation[]);
      const acts = diary
        ? dayData.acts.filter((a) => a.diary_id === diary.id)
        : ([] as DiaryActivity[]);

      const bd = breakdown(
        allocs.map((a) => ({ id: a.id, time_category_id: a.time_category_id, hours: a.hours, note: a.note })),
        categories,
      );

      return {
        machine: m,
        shiftId: rowShift ?? '',
        type: types?.find((t) => t.id === m.machine_type_id),
        diary,
        allocated: allocatedHours(
          allocs.map((a) => ({ id: a.id, time_category_id: a.time_category_id, hours: a.hours, note: a.note })),
        ),
        breakdown: bd,
        loads: acts.reduce((s, a) => s + (a.loads ?? 0), 0),
        tonnes: acts.reduce((s, a) => s + (Number(a.tonnes_estimated) || 0), 0),
      };
    });
  }, [machines, types, dayData, shifts, shiftId, categories, siteId, siteOptions]);

  const summary = useMemo(() => {
    const list = rows ?? [];
    const captured = list.filter((r) => r.diary);
    return {
      machines: list.length,
      captured: captured.length,
      missing: list.length - captured.length,
      submitted: captured.filter((r) => r.diary!.status === 'submitted').length,
      approved: captured.filter((r) => r.diary!.status === 'approved').length,
      engineHours: captured.reduce(
        (s, r) => s + ((r.diary!.meter_unit ?? 'hours') === 'hours' ? Number(r.diary!.engine_hours) || 0 : 0),
        0,
      ),
      productive: captured.reduce((s, r) => s + r.breakdown.productive, 0),
      loads: captured.reduce((s, r) => s + r.loads, 0),
      tonnes: captured.reduce((s, r) => s + r.tonnes, 0),
    };
  }, [rows]);

  function shiftDay(delta: number) {
    const d = new Date(day + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setDay(isoDate(d));
  }

  const isToday = day === isoDate();

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      {issues.length > 0 && <SyncIssues />}

      <Panel
        title={`Diary · ${fmtDate(day)}${isToday ? ' (today)' : ''}`}
        actions={
          <div className="date-nav">
            <Button variant="ghost" style={{ minHeight: 32 }} onClick={() => shiftDay(-1)}>‹ Prev</Button>
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} style={{ minHeight: 32 }} />
            <Button variant="ghost" style={{ minHeight: 32 }} onClick={() => shiftDay(1)}>Next ›</Button>
            {!isToday && (
              <Button variant="secondary" style={{ minHeight: 32 }} onClick={() => setDay(isoDate())}>
                Today
              </Button>
            )}
          </div>
        }
      >
        <div className="row" style={{ marginBottom: 'var(--sp-3)', flexWrap: 'wrap' }}>
          <select
            aria-label="Site"
            value={siteId}
            onChange={(e) => chooseSite(e.target.value)}
            style={{ minHeight: 36 }}
          >
            {siteOptions.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
            <option value={ALL_SITES}>All sites</option>
          </select>
          {visibleShifts.map((s) => (
            <button
              key={s.id}
              className={`btn ${shiftId === s.id ? '' : 'btn--secondary'}`}
              style={{ minHeight: 36 }}
              onClick={() => setShiftId(s.id)}
            >
              {s.name}
            </button>
          ))}
          <span className="spacer" />
          <Button variant="secondary" onClick={() => navigate('/diary/lists/shifts')}>Shifts & operators</Button>
          {!online && <StatusChip tone="amber">Offline — capture still works</StatusChip>}
        </div>

        <div className="grid grid--stats">
          <StatTile label="Captured" value={summary.captured} sub={`of ${summary.machines} machines`}
            rag={summary.missing === 0 ? 'green' : 'amber'} />
          <StatTile label="Missing" value={summary.missing}
            rag={summary.missing > 0 ? 'amber' : 'green'} />
          <StatTile label="Approved" value={summary.approved} />
          <StatTile label="Engine hours" value={summary.engineHours} decimals={1} />
          <StatTile label="Productive" value={summary.productive} decimals={1} unit="h" />
          <StatTile label="Loads" value={summary.loads} />
          <StatTile label="Estimated" value={summary.tonnes} unit="t" />
        </div>
      </Panel>

      <Panel title="Machines" flush>
        {!rows ? (
          <div className="centered" style={{ padding: 'var(--sp-5)' }}><span className="spinner" /></div>
        ) : rows.length === 0 ? (
          <div className="placeholder" style={{ border: 'none' }}>
            {siteId === QUARRY_SITE_ID || siteId === ALL_SITES
              ? 'No active machines in the register yet. Add the fleet first.'
              : `No active vehicles at ${siteName(siteId)}. Add them in Fleet, at this branch.`}
          </div>
        ) : (
          rows.map((r) => (
            <div
              className="day-machine"
              key={r.machine.id}
              role="button"
              tabIndex={0}
              onClick={() =>
                navigate(
                  r.diary
                    ? `/diary/${r.diary.id}`
                    : `/diary/new?date=${day}&machine=${r.machine.id}&shift=${r.shiftId}`,
                )
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  navigate(r.diary ? `/diary/${r.diary.id}`
                    : `/diary/new?date=${day}&machine=${r.machine.id}&shift=${r.shiftId}`);
                }
              }}
            >
              <div className="day-machine__main">
                <span className="day-machine__fleet">{r.machine.fleet_no}</span>
                <span className="day-machine__type">
                  {r.type?.name ?? ''}
                  {siteId === ALL_SITES && ` · ${siteName(diarySiteOf(r.machine))}`}
                </span>
                {r.diary ? (
                  <>
                    <span className="day-machine__hours">
                      {r.diary.meter_unit === 'km'
                        ? `${fmtNum(r.diary.engine_hours, 0)} km driven`
                        : `${fmtHours(r.diary.engine_hours)} h engine`}{' '}
                      · {fmtHours(r.allocated)} h allocated
                    </span>
                    {r.loads > 0 && (
                      <span className="day-machine__hours">
                        {r.loads} loads · {fmtTonnes(r.tonnes)} t
                      </span>
                    )}
                  </>
                ) : (
                  <span className="day-machine__hours">Not captured</span>
                )}
              </div>

              <div className="row" style={{ gap: 'var(--sp-2)' }}>
                {r.diary ? (
                  <>
                    <StatusChip tone={STATUS_TONE[r.diary.status]}>{humanise(r.diary.status)}</StatusChip>
                    {!r.diary.synced_at && !online && <StatusChip tone="amber">Queued</StatusChip>}
                    <Button variant="ghost" style={{ minHeight: 34 }}>
                      {r.diary.status === 'submitted' && canApprove ? 'Review' : 'Open'}
                    </Button>
                  </>
                ) : (
                  <Button style={{ minHeight: 34 }}>Capture</Button>
                )}
              </div>
            </div>
          ))
        )}
      </Panel>

      {exceptions.length > 0 && (
        <Panel title={`Data quality · ${exceptions.length} to look at`}>
          <p className="small muted" style={{ marginTop: 0 }}>
            These do not block capture. They are the things a supervisor should
            look at before the day is closed.
          </p>
          <div className="issue-list">
            {exceptions.slice(0, 20).map((e, i) => (
              <div className="issue issue--warn" key={i}>
                <strong style={{ minWidth: 110 }}>{e.fleet_no}</strong>
                <span>{e.detail}</span>
                <span className="spacer" />
                <span className="small muted">{humanise(e.exception_type)}</span>
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
